import type { Environment, SSEEvent, ChatMessage, ToolCall } from '../types/debugger';

const MOCK = true;

const MOCK_RESPONSES = [
  "I found the issue. The strategy S-5678 for advertiser A-12345 is stuck in GENERATED state because the SQS message was processed but the Bedrock invocation timed out during the discovery cadence. The Worker Lambda hit the 900s timeout while waiting for the LLM response.\n\nHere's what happened:\n1. EventBridge triggered the cadence at 02:00 UTC\n2. SQS message was dequeued by the Worker\n3. AgentCore started processing but Bedrock was throttled\n4. After 3 retries, the Lambda timed out\n5. The message went to DLQ (currently unmonitored - P0 gap)\n\n**Recommended fix:** Redrive the DLQ message and monitor for Bedrock throttling.",
  "Looking at the Output Cache for advertiser A-12345...\n\nThe cache shows stale data from 48 hours ago. This is because:\n- Last successful discovery was 2 days ago\n- The scheduled cadence has been failing (Bedrock throttling)\n- No alarm exists for cache staleness (this is a known P1 gap)\n\nThe advertiser is seeing outdated strategy pills in the SSW widget because the Proxy Lambda reads from this stale cache.",
];

const MOCK_TOOL_CALLS: ToolCall[] = [
  { id: 'tc-001', name: 'search_spb_agent_logs', status: 'complete', input: { advertiser_id: 'A-12345', hours: 24 }, output: 'Found 47 log entries', duration: 1200 },
  { id: 'tc-002', name: 'check_output_cache', status: 'complete', input: { advertiser_id: 'A-12345' }, output: 'Cache entry found, last updated 48h ago', duration: 340 },
  { id: 'tc-003', name: 'get_cs3_execution', status: 'complete', input: { advertiser_id: 'A-12345', limit: 10 }, output: '3 executions found', duration: 280 },
];

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export type StreamCallback = (event: SSEEvent) => void;

export async function streamAgentChat(
  env: Environment,
  message: string,
  sessionId: string,
  onEvent: StreamCallback,
): Promise<void> {
  if (MOCK) {
    // Simulate reasoning
    await delay(500);
    onEvent({ event: 'reasoning', payload: { text: 'Analyzing the advertiser data...' } });

    // Simulate tool calls
    for (const tc of MOCK_TOOL_CALLS) {
      await delay(300);
      onEvent({ event: 'tool_call', payload: { id: tc.id, name: tc.name, input: tc.input } });
      await delay(tc.duration || 500);
      onEvent({ event: 'tool_response', payload: { id: tc.id, name: tc.name, output: tc.output, duration: tc.duration } });
    }

    // Simulate streaming response
    const text = MOCK_RESPONSES[Math.floor(Math.random() * MOCK_RESPONSES.length)];
    for (let i = 0; i < text.length; i += 5) {
      await delay(15);
      onEvent({ event: 'response', payload: { text: text.slice(i, i + 5) } });
    }
    onEvent({ event: 'final_response', payload: { text } });
    return;
  }

  // Real SSE via fetch + ReadableStream
  const res = await fetch(`/api/${env.toLowerCase()}/debugger/agent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, sessionId }),
  });

  if (!res.ok) {
    onEvent({ event: 'error', payload: { message: `Agent request failed: ${res.status}` } });
    return;
  }

  if (!res.body) {
    onEvent({ event: 'error', payload: { message: 'No response body' } });
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed === '[DONE]') continue;
      const data = trimmed.startsWith('data: ') ? trimmed.slice(6) : trimmed;
      try { onEvent(JSON.parse(data)); } catch (e) { console.warn('SSE parse error:', data, e); }
    }
  }
}
